"use client";

import { useCallback, useEffect, useRef } from "react";

interface UseSessionArtifactArchiveOptions {
  roomName?: string | null;
  occurrenceId?: number | null;
  breakoutRoomId?: string | null;
  notes: string;
  enabled: boolean;
}

export function useSessionArtifactArchive({
  roomName,
  occurrenceId,
  breakoutRoomId,
  notes,
  enabled
}: UseSessionArtifactArchiveOptions) {
  const whiteboardTimer = useRef<number | null>(null);
  const notesTimer = useRef<number | null>(null);
  const lastSubmittedNotes = useRef<string | null>(null);
  const latestWhiteboard = useRef<Blob | null>(null);
  const finalSave = useRef<Promise<void> | null>(null);
  const sessionKey = useRef<string | null>(null);
  const endpoint = roomName ? `/api/meeting/${encodeURIComponent(roomName)}/artifacts` : null;

  const getSessionKey = () => {
    sessionKey.current ??= globalThis.crypto.randomUUID();
    return sessionKey.current;
  };

  const uploadNotes = useCallback(
    async (content: string) => {
      if (!endpoint || !occurrenceId) return;
      const form = new FormData();
      form.set("occurrenceId", String(occurrenceId));
      if (breakoutRoomId) form.set("breakoutRoomId", breakoutRoomId);
      form.set("sessionKey", getSessionKey());
      form.set("type", "notes");
      form.set("content", content);
      const response = await fetch(endpoint, { method: "POST", body: form, signal: AbortSignal.timeout(15000) });
      if (!response.ok) throw new Error(`Notes archive failed (${response.status})`);
      lastSubmittedNotes.current = content;
    },
    [endpoint, occurrenceId, breakoutRoomId]
  );

  const uploadWhiteboard = useCallback(
    async (snapshot: Blob) => {
      if (!endpoint || !occurrenceId) return;
      const form = new FormData();
      form.set("occurrenceId", String(occurrenceId));
      if (breakoutRoomId) form.set("breakoutRoomId", breakoutRoomId);
      else form.set("sessionKey", getSessionKey());
      form.set("type", "whiteboard");
      form.set("file", snapshot, "whiteboard.png");
      const response = await fetch(endpoint, { method: "POST", body: form, signal: AbortSignal.timeout(15000) });
      if (!response.ok) throw new Error(`Whiteboard archive failed (${response.status})`);
    },
    [endpoint, occurrenceId, breakoutRoomId]
  );

  useEffect(() => {
    if (!endpoint || !occurrenceId || !enabled || !notes.trim()) return;
    if (lastSubmittedNotes.current === notes) return;
    notesTimer.current = window.setTimeout(() => {
      void uploadNotes(notes).catch((error: unknown) => console.warn("Unable to archive shared notes", error));
      notesTimer.current = null;
    }, 650);
    return () => {
      if (notesTimer.current) window.clearTimeout(notesTimer.current);
      notesTimer.current = null;
    };
  }, [endpoint, occurrenceId, enabled, notes, uploadNotes]);

  const saveWhiteboardSnapshot = useCallback(
    (snapshot: Blob) => {
      if (!endpoint || !occurrenceId || !enabled) return;
      latestWhiteboard.current = snapshot;
      if (whiteboardTimer.current) window.clearTimeout(whiteboardTimer.current);
      whiteboardTimer.current = window.setTimeout(() => {
        void uploadWhiteboard(snapshot).catch((error: unknown) => console.warn("Unable to archive whiteboard", error));
        whiteboardTimer.current = null;
      }, 250);
    },
    [endpoint, occurrenceId, enabled, uploadWhiteboard]
  );

  const flushFinalSave = useCallback(
    (finalNotes: string, finalSnapshot: Blob | null): Promise<void> => {
      if (!endpoint || !occurrenceId || !enabled) return Promise.resolve();
      if (finalSave.current) return finalSave.current;

      if (notesTimer.current) window.clearTimeout(notesTimer.current);
      notesTimer.current = null;
      if (whiteboardTimer.current) window.clearTimeout(whiteboardTimer.current);
      whiteboardTimer.current = null;

      const snapshot = finalSnapshot ?? latestWhiteboard.current;
      const uploads = [
        ...(finalNotes.trim() ? [uploadNotes(finalNotes)] : []),
        ...(snapshot ? [uploadWhiteboard(snapshot)] : [])
      ];
      const pending = Promise.allSettled(uploads).then((results) => {
        for (const result of results) {
          if (result.status === "rejected") console.warn("Final session artifact save failed", result.reason);
        }
      });
      finalSave.current = pending;
      void pending.finally(() => {
        if (finalSave.current === pending) finalSave.current = null;
      });
      return pending;
    },
    [endpoint, occurrenceId, enabled, uploadNotes, uploadWhiteboard]
  );

  useEffect(
    () => () => {
      if (whiteboardTimer.current) window.clearTimeout(whiteboardTimer.current);
      if (notesTimer.current) window.clearTimeout(notesTimer.current);
    },
    []
  );

  return { saveWhiteboardSnapshot, flushFinalSave };
}
